import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { userAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { RewardedGate } from '../../components/RewardedGate';
import { GoogleAdBanner } from '../../components/GoogleAds';
import { WelcomeScreen } from '../../components/WelcomeScreen';
import { CheckCircle, Lock, Play, Star, Trophy, Zap, Image } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export function HomePage() {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdGate, setShowAdGate] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [pendingQuizIndex, setPendingQuizIndex] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  // Check if we should show welcome screen (once per session)
  useEffect(() => {
    const hasSeenWelcome = sessionStorage.getItem('hasSeenWelcome');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, []);

  // Reload pack whenever component mounts or location changes (e.g., navigating back from quiz)
  useEffect(() => {
    loadDailyPack();
  }, [location.key, i18n.language]); // topic names come in the app language

  const loadDailyPack = async () => {
    setLoading(true);
    try {
      const res = await userAPI.getTodayPack();
      setPack(res.data);
    } catch (error) {
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomeClose = () => {
    sessionStorage.setItem('hasSeenWelcome', 'true');
    setShowWelcome(false);
  };

  const getQuizStatus = (quiz) => {
    if (!quiz.user_result) return 'available';
    if (quiz.user_result.attempts_used >= 3 || quiz.user_result.locked) return 'completed';
    return 'retry';
  };

  const handleStartQuiz = (quizIndex) => {
    // Check how many quizzes user has completed today
    const completedCount = pack.quizzes.filter(q => getQuizStatus(q) === 'completed').length;
    
    // Show ad ONLY if user has already completed at least 1 quiz today
    // This means: first quiz = no ad, subsequent quizzes = ad before starting
    if (completedCount > 0) {
      setPendingQuizIndex(quizIndex);
      setShowAdGate(true);
    } else {
      // First quiz of the day - no ad, go directly
      navigate(`/quiz/${quizIndex}`);
    }
  };

  const handleAdComplete = () => {
    setShowAdGate(false);
    if (pendingQuizIndex !== null) {
      navigate(`/quiz/${pendingQuizIndex}`);
      setPendingQuizIndex(null);
    }
  };

  // Show welcome screen
  if (showWelcome) {
    return (
      <WelcomeScreen 
        onStart={handleWelcomeClose}
        userName={user?.nickname || user?.username}
      />
    );
  }

  // Show ad gate before next quiz
  if (showAdGate) {
    return (
      <RewardedGate
        seconds={10}
        onComplete={handleAdComplete}
        onCancel={() => {
          setShowAdGate(false);
          setPendingQuizIndex(null);
        }}
        title={t('home.nextQuizAd') || 'Next Quiz'}
        description={t('home.watchAdForNextQuiz') || 'Watch a short ad to start your next quiz'}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-400">{t('home.noQuizzes')}</p>
      </div>
    );
  }

  const completedCount = pack.quizzes.filter(q => getQuizStatus(q) === 'completed').length;
  const bonusUnlocked = completedCount >= 10;

  return (
    <div className="space-y-6 pb-4">
      {/* Header */}
      <div className="text-center mb-8">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-white mb-2"
        >
          {t('home.title')}
        </motion.h1>
        <p className="text-slate-400">{t('home.subtitle')}</p>
      </div>

      {/* Progress Bar */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700/50"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-400">{t('home.progress')}</span>
          <span className="text-lg font-bold text-teal-400">{completedCount}/10</span>
        </div>
        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${(completedCount / 10) * 100}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"
          />
        </div>
        {completedCount >= 10 && (
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-teal-400 mt-2 flex items-center gap-2"
          >
            <Star className="w-4 h-4 fill-current" />
            {t('home.bonusUnlocked')}
          </motion.p>
        )}
      </motion.div>

      {/* Quiz Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {pack.quizzes.slice(0, 10).map((quiz, index) => {
          const status = getQuizStatus(quiz);
          const isImageQuiz = quiz.topic_names?.some(name => name.toLowerCase().includes('image'));
          const score = quiz.user_result?.best_correct;
          const scorePercent = score ? Math.round((score / 30) * 100) : 0;
          
          return (
            <motion.div
              key={quiz.index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.02, y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleStartQuiz(quiz.index)}
              className={`relative cursor-pointer rounded-2xl p-4 border transition-all overflow-hidden ${
                status === 'completed'
                  ? 'bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border-teal-400/50 shadow-lg shadow-teal-500/10'
                  : status === 'retry'
                  ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-400/50'
                  : 'bg-slate-800/50 border-slate-600/50 hover:border-teal-400/50 hover:bg-slate-700/50'
              }`}
              data-testid={`quiz-card-${quiz.index}`}
            >
              {/* Status indicator bar at top */}
              <div className={`absolute top-0 left-0 right-0 h-1 ${
                status === 'completed' 
                  ? 'bg-gradient-to-r from-teal-400 to-cyan-400' 
                  : status === 'retry'
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400'
                  : 'bg-slate-600'
              }`} />
              
              {/* Quiz Number */}
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3 ${
                status === 'completed'
                  ? 'bg-gradient-to-br from-teal-400 to-cyan-500 text-white shadow-lg shadow-teal-500/30'
                  : status === 'retry'
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                  : 'bg-gradient-to-br from-slate-600 to-slate-700 text-slate-300 hover:from-teal-500 hover:to-cyan-500'
              }`}>
                {status === 'completed' ? (
                  <CheckCircle className="w-7 h-7" />
                ) : isImageQuiz ? (
                  <Image className="w-7 h-7" />
                ) : (
                  <span className="text-2xl font-bold">{quiz.index + 1}</span>
                )}
              </div>

              {/* Quiz Info */}
              <div className="text-center">
                <p className="font-bold text-white text-sm mb-1">
                  {t('home.quiz')} {quiz.index + 1}
                </p>
                
                {/* Score display for completed quizzes */}
                {status === 'completed' && quiz.user_result && (
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-teal-400">
                      {scorePercent}%
                    </p>
                    <p className="text-xs text-slate-400">
                      {score}/30 {t('home.correct') || 'correct'}
                    </p>
                  </div>
                )}
                
                {/* Retry state */}
                {status === 'retry' && quiz.user_result && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-400">
                      {scorePercent}%
                    </p>
                    <p className="text-xs text-amber-300/80">
                      {3 - quiz.user_result.attempts_used} {t('home.attemptsLeft')}
                    </p>
                  </div>
                )}
                
                {/* Available state */}
                {status === 'available' && (
                  <p className="text-xs text-teal-400 font-medium">
                    {t('home.tapToStart')}
                  </p>
                )}
              </div>

              {/* Image Quiz Badge */}
              {isImageQuiz && (
                <div className="absolute -top-1 -right-1 bg-gradient-to-br from-purple-500 to-violet-600 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-xl shadow-lg">
                  📷
                </div>
              )}
              
              {/* Completed checkmark overlay */}
              {status === 'completed' && (
                <div className="absolute bottom-2 right-2 text-teal-400/50">
                  <CheckCircle className="w-5 h-5" />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Bonus Quiz */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className={`rounded-2xl p-6 border ${
          bonusUnlocked
            ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30 cursor-pointer'
            : 'bg-slate-800/30 border-slate-700/50'
        }`}
        onClick={() => bonusUnlocked && handleStartQuiz(10)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              bonusUnlocked
                ? 'bg-gradient-to-br from-yellow-500 to-orange-500 text-white'
                : 'bg-slate-700 text-slate-500'
            }`}>
              {bonusUnlocked ? <Zap className="w-7 h-7" /> : <Lock className="w-7 h-7" />}
            </div>
            <div>
              <h3 className={`font-bold text-lg ${
                bonusUnlocked ? 'text-yellow-400' : 'text-slate-500'
              }`}>
                {t('home.bonusQuiz')}
              </h3>
              <p className="text-sm text-slate-400">
                {bonusUnlocked ? t('home.bonusReady') : t('home.complete10')}
              </p>
            </div>
          </div>
          {bonusUnlocked && (
            <Button className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600">
              <Play className="w-4 h-4 mr-2" />
              {t('home.play')}
            </Button>
          )}
        </div>
      </motion.div>
      <GoogleAdBanner />
    </div>
  );
}
